import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Select, Textarea, useToast, Modal, Badge, DataTable, Column, EmptyState, LongText, NameCell } from '../../../shared/components';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { getSettingsRepository } from '../../../shared/services/settings/repositoryFactory';
import { ApplicationMember } from '../../../shared/services/settings/types';
import {
  Inconsistency,
  InconsistencyStatus,
  InconsistencySummary,
  LocationPing,
  NOVELTY_TYPE_LABEL,
  NoveltyType,
  PING_STATUS_LABEL,
  PingStatus,
} from '../../../shared/services/turnoGeo/types';
import { InfoTip } from './InfoTip';
import { shortDateTime } from '../jornadaUtil';

const repo = getTurnoGeoRepository();
const settingsRepo = getSettingsRepository();
const LIMIT = 100;

const SEVERITY_TONE = { ALTA: 'critical', MEDIA: 'warning', BAJA: 'neutral' } as const;
const STATUS_TONE: Record<InconsistencyStatus, 'warning' | 'success' | 'info' | 'neutral'> = { PENDIENTE: 'warning', REVISADA: 'info', JUSTIFICADA: 'success', DESCARTADA: 'neutral' };
const STATUS_LABEL: Record<InconsistencyStatus, string> = { PENDIENTE: 'Pendiente', REVISADA: 'Revisada', JUSTIFICADA: 'Justificada', DESCARTADA: 'Descartada' };
const PING_TONE: Record<PingStatus, 'warning' | 'success' | 'critical' | 'neutral'> = { PENDIENTE: 'warning', RESPONDIDO: 'success', NO_RESPONDIDO: 'critical', CANCELADO: 'neutral' };

/**
 * Configuración > Inconsistencias por auditar (solo administrador). Lista lo que el sistema
 * encontró raro (almuerzo largo, toque fuera de rango o sin respuesta, reloj alterado, turno sin
 * cierre, marcación faltante, día no laboral, fuera de tolerancia) para que alguien lo revise y lo
 * marque como revisada, justificada o descartada. Cada vez que se abre, el servidor revisa lo
 * pendiente en el tiempo antes de mostrar.
 */
export function InconsistenciesTab() {
  const toast = useToast();
  const [view, setView] = useState<'inconsistencias' | 'toques'>('inconsistencias');
  const [members, setMembers] = useState<ApplicationMember[]>([]);
  const [summary, setSummary] = useState<InconsistencySummary | null>(null);

  const [rows, setRows] = useState<Inconsistency[] | null>(null);
  const [total, setTotal] = useState(0);
  const [userId, setUserId] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('PENDIENTE');
  const [severity, setSeverity] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [pings, setPings] = useState<LocationPing[] | null>(null);
  const [pingTotal, setPingTotal] = useState(0);
  const [pingStatus, setPingStatus] = useState('');

  const [reviewing, setReviewing] = useState<Inconsistency | null>(null);
  const [detail, setDetail] = useState<Inconsistency | null>(null);
  const [reviewStatus, setReviewStatus] = useState<InconsistencyStatus>('REVISADA');
  const [reviewNote, setReviewNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const loadSummary = useCallback(() => {
    repo.inconsistencySummary().then(setSummary).catch(() => setSummary(null));
  }, []);

  const search = useCallback(
    (scan: boolean) => {
      setRows(null);
      repo
        .listInconsistencies(
          { userId: userId || undefined, code: code || undefined, status: status || undefined, severity: severity || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, page: 1, pageSize: LIMIT },
          scan
        )
        .then((r) => {
          setRows(r.items);
          setTotal(r.total);
          loadSummary();
        })
        .catch((err) => {
          setRows([]);
          toast.show(err instanceof Error ? err.message : 'No se pudieron cargar las inconsistencias.', 'error');
        });
    },
    [userId, code, status, severity, dateFrom, dateTo, toast, loadSummary]
  );

  const searchPings = useCallback(() => {
    setPings(null);
    repo
      .listPings({
        userId: userId || undefined,
        status: (pingStatus || undefined) as PingStatus | undefined,
        dateFrom: dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : undefined,
        dateTo: dateTo ? new Date(`${dateTo}T23:59:59.999`).toISOString() : undefined,
        page: 1,
        pageSize: LIMIT,
      })
      .then((r) => {
        setPings(r.items);
        setPingTotal(r.total);
      })
      .catch((err) => {
        setPings([]);
        toast.show(err instanceof Error ? err.message : 'No se pudieron cargar los toques.', 'error');
      });
  }, [userId, pingStatus, dateFrom, dateTo, toast]);

  useEffect(() => {
    settingsRepo.listApplicationMembers('turno-geo', true).then((m) => setMembers(m.filter((x) => x.hasAccess)));
    search(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (view === 'toques' && pings === null) searchPings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const openReview = (r: Inconsistency) => {
    setReviewing(r);
    setReviewStatus(r.status === 'PENDIENTE' ? (r.coveredByNovelty ? 'JUSTIFICADA' : 'REVISADA') : r.status);
    setReviewNote(r.reviewNote ?? '');
  };
  const saveReview = async () => {
    if (!reviewing) return;
    setIsSaving(true);
    try {
      await repo.reviewInconsistency(reviewing.id, { status: reviewStatus, note: reviewNote });
      toast.show('Revisión guardada.');
      setReviewing(null);
      search(false);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const columns: Column<Inconsistency>[] = [
    {
      key: 'fecha', filterValue: (r) => r.workDate,
      header: 'Cuándo',
      sortable: true,
      sortValue: (r) => r.occurredAt,
      render: (r) => (
        <div className="whitespace-nowrap">
          <p className="font-medium text-ink-800">{shortDateTime(r.occurredAt)}</p>
          <p className="text-xs text-ink-400">Día {r.workDate}</p>
        </div>
      ),
    },
    {
      key: 'empleado',
      header: 'Empleado',
      sortable: true,
      sortValue: (r) => r.userFullName,
      render: (r) => <NameCell name={r.userFullName} />,
    },
    {
      key: 'tipo', filterValue: (r) => r.codeLabel,
      header: 'Qué pasó',
      render: (r) => (
        <div className="max-w-[340px]">
          <p className="flex flex-wrap items-center gap-1.5 font-medium text-ink-800">
            {r.codeLabel} <Badge tone={SEVERITY_TONE[r.severity]}>{r.severity}</Badge>
          </p>
          <p className="mt-0.5 text-xs text-ink-600"><LongText text={r.description} max={90} /></p>
          {r.coveredByNovelty && (
            <p className="mt-1 text-[11px] text-brand-700">Ese día tiene una novedad aprobada: {NOVELTY_TYPE_LABEL[r.coveredByNovelty as NoveltyType] ?? r.coveredByNovelty}.</p>
          )}
        </div>
      ),
    },
    {
      key: 'estado', filterValue: (r) => STATUS_LABEL[r.status],
      header: 'Estado',
      render: (r) => (
        <div>
          <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
          {r.reviewedBy && <p className="mt-0.5 text-[11px] text-ink-400">{r.reviewedBy}</p>}
          {r.reviewNote && <p className="max-w-[200px] text-[11px] text-ink-500"><LongText text={r.reviewNote} max={60} /></p>}
        </div>
      ),
    },
    {
      key: 'acc',
      header: '',
      render: (r) => (
        <div className="flex gap-2">
          <Button variant={r.status === 'PENDIENTE' ? 'primary' : 'outline'} size="md" onClick={() => openReview(r)}>{r.status === 'PENDIENTE' ? 'Revisar' : 'Cambiar'}</Button>
          <Button variant="ghost" size="md" onClick={() => setDetail(r)}>Evidencia</Button>
        </div>
      ),
    },
  ];

  const pingColumns: Column<LocationPing>[] = [
    { key: 'f', filterValue: (r) => r.scheduledAt.slice(0, 10), header: 'Programado', sortable: true, sortValue: (r) => r.scheduledAt, render: (r) => <span className="whitespace-nowrap">{shortDateTime(r.scheduledAt)}</span> },
    { key: 'e', header: 'Empleado', sortable: true, sortValue: (r) => r.userFullName, render: (r) => <NameCell name={r.userFullName} />,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Resumen */}
      <div className="rounded-xl border border-ink-100 bg-surface p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-ink-800">
          Pendientes por revisar: {summary ? summary.pending : '…'}
          <InfoTip title="¿De dónde salen?" text="El sistema las crea solo: al marcar (almuerzo largo, reloj alterado, día no laboral, tarde/temprano), al responder un toque de ubicación (fuera de rango) y al revisar el paso del tiempo (toque sin respuesta, turno sin cierre, almuerzo sin regreso). Nunca se borran: se revisan." />
        </p>
        {summary && summary.byCode.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {summary.byCode.map((b) => (
              <button key={b.code} type="button" onClick={() => { setStatus('PENDIENTE'); setCode(b.code); setView('inconsistencias'); setTimeout(() => search(false), 0); }} className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100">
                {b.label}: {b.count}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Button size="md" variant={view === 'inconsistencias' ? 'primary' : 'outline'} onClick={() => setView('inconsistencias')}>Inconsistencias</Button>
        <Button size="md" variant={view === 'toques' ? 'primary' : 'outline'} onClick={() => setView('toques')}>Toques de ubicación</Button>
      </div>

      {/* Filtros */}
      <div className="grid gap-3 rounded-xl border border-ink-100 bg-surface p-4 sm:grid-cols-2 lg:grid-cols-6">
        <Select label="Empleado" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="Todos" options={members.map((m) => ({ value: m.id, label: m.fullName }))} />
        {view === 'inconsistencias' ? (
          <>
            <Select
              label="Tipo"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Todos"
              options={[
                ['ALMUERZO_EXCEDIDO', 'Almuerzo excedido'], ['ALMUERZO_FUERA_ZONA', 'Almuerzo fuera de zona'], ['TOQUE_FUERA_RANGO', 'Toque fuera de rango'], ['TOQUE_SIN_RESPUESTA', 'Toque sin respuesta'],
                ['ENTRADA_SIN_SALIDA', 'Entrada sin salida'], ['MARCACION_FALTANTE', 'Marcación faltante'], ['DIA_NO_LABORAL', 'Día no laboral'], ['FUERA_DE_TOLERANCIA', 'Fuera de tolerancia'],
                ['TURNO_EXCEDIDO', 'Turno excedido'], ['RELOJ_ALTERADO', 'Reloj alterado'], ['MODALIDAD_NO_COINCIDE', 'Declarado ≠ detectado'],
              ].map(([value, label]) => ({ value, label }))}
            />
            <Select label="Estado" value={status} onChange={(e) => setStatus(e.target.value)} placeholder="Todos" options={(Object.keys(STATUS_LABEL) as InconsistencyStatus[]).map((s) => ({ value: s, label: STATUS_LABEL[s] }))} />
            <Select label="Gravedad" value={severity} onChange={(e) => setSeverity(e.target.value)} placeholder="Todas" options={[{ value: 'ALTA', label: 'Alta' }, { value: 'MEDIA', label: 'Media' }, { value: 'BAJA', label: 'Baja' }]} />
          </>
        ) : (
          <Select label="Estado del toque" value={pingStatus} onChange={(e) => setPingStatus(e.target.value)} placeholder="Todos" options={(Object.keys(PING_STATUS_LABEL) as PingStatus[]).map((s) => ({ value: s, label: PING_STATUS_LABEL[s] }))} />
        )}
        <Input label="Desde" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <Input label="Hasta" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        <div className="sm:col-span-2 lg:col-span-6">
          <Button size="md" onClick={() => (view === 'inconsistencias' ? search(true) : searchPings())}>Buscar</Button>
          <span className="ml-3 text-xs text-ink-400">
            {view === 'inconsistencias'
              ? rows ? (total > LIMIT ? `Mostrando ${LIMIT} de ${total}. Afina los filtros.` : `${total} resultado(s).`) : 'Revisando…'
              : pings ? (pingTotal > LIMIT ? `Mostrando ${LIMIT} de ${pingTotal}. Afina los filtros.` : `${pingTotal} toque(s).`) : 'Cargando…'}
          </span>
        </div>
      </div>

      {view === 'inconsistencias' ? (
        rows && rows.length === 0 ? (
          <EmptyState icon="✅" title="Nada por revisar" description="No hay inconsistencias con esos filtros." />
        ) : (
          <DataTable excel fixedKey="empleado" columns={columns} rows={rows ?? []} rowKey={(r) => r.id} isLoading={rows === null} defaultSortKey="fecha" />
        )
      ) : pings && pings.length === 0 ? (
        <EmptyState icon="📍" title="Sin toques" description="Todavía no se han sorteado toques con esos filtros. Se sortean al marcar la entrada y el regreso del almuerzo." />
      ) : (
        <DataTable excel fixedKey="empleado" columns={pingColumns} rows={pings ?? []} rowKey={(r) => r.id} isLoading={pings === null} defaultSortKey="f" />
      )}

      {/* Revisar */}
      <Modal open={!!reviewing} onClose={() => setReviewing(null)} title="Revisar inconsistencia">
        {reviewing && (
          <div className="space-y-3">
            <div className="rounded-xl bg-ink-50 p-3 text-xs text-ink-600">
              <p className="font-semibold text-ink-800">{reviewing.userFullName} · {reviewing.codeLabel}</p>
              <p className="mt-1">{reviewing.description}</p>
              {reviewing.coveredByNovelty && <p className="mt-1 text-brand-700">Ese día tiene una novedad aprobada ({NOVELTY_TYPE_LABEL[reviewing.coveredByNovelty as NoveltyType] ?? reviewing.coveredByNovelty}): puede ser motivo para justificarla.</p>}
            </div>
            <Select
              label="Resultado de la revisión"
              value={reviewStatus}
              onChange={(e) => setReviewStatus(e.target.value as InconsistencyStatus)}
              options={[
                { value: 'REVISADA', label: 'Revisada (se confirmó, queda como antecedente)' },
                { value: 'JUSTIFICADA', label: 'Justificada (tiene una razón válida)' },
                { value: 'DESCARTADA', label: 'Descartada (fue un falso positivo)' },
                { value: 'PENDIENTE', label: 'Volver a pendiente' },
              ]}
            />
            <Textarea label="Nota" value={reviewNote} maxLength={300} onChange={(e) => setReviewNote(e.target.value)} hint="Obligatoria (mínimo 5 caracteres) salvo al volver a pendiente. Queda con tu nombre y la fecha." />
            <div className="flex gap-3 pt-1">
              <Button variant="outline" fullWidth onClick={() => setReviewing(null)}>Cancelar</Button>
              <Button fullWidth onClick={saveReview} isLoading={isSaving} disabled={reviewStatus !== 'PENDIENTE' && reviewNote.trim().length < 5}>Guardar</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Evidencia */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title="Evidencia">
        {detail && (
          <div className="space-y-3">
            <p className="text-xs text-ink-500">{detail.userFullName} · {detail.codeLabel} · {shortDateTime(detail.occurredAt)}</p>
            <p className="text-sm text-ink-700">{detail.description}</p>
            <pre className="max-h-64 overflow-auto rounded-xl bg-ink-50 p-3 text-[11px] text-ink-600">{JSON.stringify(detail.evidence ?? { sinDatosAdicionales: true }, null, 2)}</pre>
            <Button variant="outline" fullWidth onClick={() => setDetail(null)}>Cerrar</Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
